/**
 * FleetManager V3 - Main Entry Point
 * Adaptive UI based on user profile
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';

// i18n — must be initialised before any component renders (reads localStorage.fm_lang).
import './i18n/index.js';

// Centralized React Query configuration
import { defaultQueryClientOptions } from './lib/queryConfig.js';

// Preflight health checks
import { runPreflightChecks, showPreflightError, showOptionalWarnings } from './utils/preflight.js';

// One-shot token migration — MUST run before any React provider mounts.
// When this lived inside AuthContext's useEffect, ProfileProvider /
// PipelineProvider / AppContent had already fired their first API calls
// with the stale pre-migration token, triggering a cascade of 401s
// (race condition). Running it synchronously at the entry point
// guarantees localStorage is clean by the time React renders.
import { migrateTokensIfNeeded } from './utils/tokenMigration.js';

// V3 App
import { App } from './v3/App.jsx';

// Mobile PWA App
import MobileApp from './mobile/MobileApp.jsx';

// Public pages (no auth required)
import ShareViewer from './components/shares/ShareViewer.jsx';

// Re-export hooks for V1/V2 component compatibility
export {
  useVehicles,
  useVehicle,
  useActiveVehicles,
  useDrivers,
  useDriver,
  useActiveDrivers,
  useClaims,
  useMaintenance,
  useInsurance,
  useFuelLogs,
  useInspections,
  useAssignments,
  useOrganizations,
  useWorkingSites,
  useTrips,
  useEvents,
  useEvent,
  useExpenses,
  useExpense,
  useExpensesByEvent,
  useDocuments,
  useAllVehicleDocuments,
  USE_MOCK_DATA
} from './hooks/useDataHooks.js';

// View constants for navigation
export const VIEWS = {
  ORGANIZATIONS: 'Organizations',
  APP_RADAR: 'App Radar',
  FLEET_COCKPIT: 'Cockpit Flotte',
  COST_DASHBOARD: 'Coûts / DAF',
  PARC_VIEW: 'Vue Parc',
  GDPR_COMPLIANCE: 'Conformité RGPD',
  EMAIL_TEMPLATES: 'Emails',
  DASHBOARD: 'Tableau de bord',
  VEHICLES: 'Véhicules',
  DRIVERS: 'Conducteurs',
  TOOLS: 'Outils & Équipements',
  MAINTENANCE: 'Maintenance',
  INSPECTIONS: 'Inspections (DVIR)',
  FUEL: 'Carburant',
  CLAIMS: 'Sinistres (Workflow)',
  INSURANCE: 'Assurance',
  CONTRACTS: 'Contrats',
  EVENTS: 'Événements',
  EXPENSES: 'Dépenses',
  VEHICLE_ENTRY: 'Entrée véhicule',
  VEHICLE_EXIT: 'Sortie véhicule',
  OWNER_MANAGEMENT: 'Gestion Propriétaire',
  FINANCING: 'Financement',
  CHECK_PERMITS: 'Vérification Permis',
  DOCUMENTS: 'Documents',
  CALENDAR: 'Calendrier',
  DATABASE: 'Base de Données',
  ASSIGNMENTS: 'Affectations',
  TELEMATICS: 'Télématique',
  ADMIN: 'Statut Admin',
  FOURNISSEURS: 'Fournisseurs',
  FINES: 'Amendes',
  FLEET_MAP: 'Carte Flotte',
};

// CSV utilities for export functionality
export function arrayToCSV(data, columns) {
  if (!data || data.length === 0) return '';
  const headers = columns.map(col => col.label).join(',');
  const rows = data.map(item => {
    return columns.map(col => {
      let value = item[col.key];
      if (value === null || value === undefined) value = '';
      value = String(value);
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        value = '"' + value.replace(/"/g, '""') + '"';
      }
      return value;
    }).join(',');
  });
  return [headers, ...rows].join('\n');
}

export function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Create React Query client with centralized configuration
const queryClient = new QueryClient({
  defaultOptions: defaultQueryClientOptions
});

/**
 * Simple Router - Check URL path for public routes
 * Public routes bypass authentication
 */
function RootApp() {
  const path = window.location.pathname;

  // Mobile PWA route: /m or /m/*
  if (path === '/m' || path.startsWith('/m/')) {
    return <MobileApp />;
  }

  // Public share viewer route: /share/:token
  if (path.startsWith('/share/')) {
    const token = path.replace('/share/', '');
    return <ShareViewer token={token} />;
  }

  // Default: Authenticated app
  return <App />;
}

// Render app with preflight checks
async function startApp() {
  // Clear any stale tokens from old format BEFORE React mounts. If this is
  // deferred into a provider effect, sibling providers (Profile/Pipeline/
  // GuidedExperience) fire their initial fetches with the about-to-be-wiped
  // token and the UI floods with 401 errors.
  migrateTokensIfNeeded();

  // Run health checks before loading React
  const preflightResults = await runPreflightChecks();

  // Show warnings for optional endpoints (non-blocking)
  showOptionalWarnings(preflightResults);

  // If critical endpoints are down, show error screen instead of loading React
  if (!preflightResults.ok) {
    console.error('[Preflight] Critical services unavailable - blocking app load');
    showPreflightError(preflightResults);
    return; // Don't load React
  }

  // All good! Load React app
  console.log('[Preflight] ✅ All critical services available - loading app');
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    // <React.StrictMode> - Temporarily disabled to debug infinite loop
      <QueryClientProvider client={queryClient}>
        <RootApp />
      </QueryClientProvider>
    // </React.StrictMode>
  );
}

// Start the app
startApp().catch((err) => {
  console.error('[Preflight] Unexpected error during startup:', err);
  document.body.innerHTML = `
    <div style="padding: 40px; font-family: monospace; color: red;">
      <h1>❌ Erreur au démarrage</h1>
      <p>Une erreur inattendue est survenue lors de l'initialisation de l'application.</p>
      <pre>${err.message}\n\n${err.stack}</pre>
      <button onclick="location.reload()">Réessayer</button>
    </div>
  `;
});
